"use client";

import { useMemo, useState } from "react";
import type { JobCard, EmploymentType } from "@/lib/types";
import { EMPLOYMENT_TYPE_LABEL } from "@/lib/types";
import { cn } from "@/lib/utils";
import JobCardItem from "./JobCardItem";

type Props = {
  jobs: JobCard[];
  /** 雇用形態の絞り込みピルを表示するか（既定 true） */
  showFilter?: boolean;
  className?: string;
  emptyMessage?: string;
};

type Filter = EmploymentType | "all";

export default function JobList({
  jobs,
  showFilter = true,
  className,
  emptyMessage = "現在募集中の求人はありません。",
}: Props) {
  const [filter, setFilter] = useState<Filter>("all");

  // 実際に存在する雇用形態のみピルとして出す
  const availableTypes = useMemo(() => {
    const set = new Set<EmploymentType>();
    jobs.forEach((j) => set.add(j.employment_type));
    return (Object.keys(EMPLOYMENT_TYPE_LABEL) as EmploymentType[]).filter((t) =>
      set.has(t)
    );
  }, [jobs]);

  const filtered = useMemo(
    () => (filter === "all" ? jobs : jobs.filter((j) => j.employment_type === filter)),
    [jobs, filter]
  );

  if (jobs.length === 0) {
    return (
      <p className="rounded-xl2 border border-dashed border-line bg-mist px-6 py-16 text-center text-sm text-muted">
        {emptyMessage}
      </p>
    );
  }

  const pillBase =
    "inline-flex min-h-11 items-center rounded-full border px-4 py-1.5 text-sm font-medium transition-colors";

  return (
    <div className={className}>
      {showFilter && availableTypes.length > 1 && (
        <div
          className="mb-8 flex flex-wrap gap-2"
          role="group"
          aria-label="雇用形態で絞り込み"
        >
          <button
            type="button"
            onClick={() => setFilter("all")}
            aria-pressed={filter === "all"}
            className={cn(
              pillBase,
              filter === "all"
                ? "border-navy-700 bg-navy-700 text-white"
                : "border-line bg-white text-ink hover:border-navy-300 hover:text-navy-700"
            )}
          >
            すべて
          </button>
          {availableTypes.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setFilter(t)}
              aria-pressed={filter === t}
              className={cn(
                pillBase,
                filter === t
                  ? "border-navy-700 bg-navy-700 text-white"
                  : "border-line bg-white text-ink hover:border-navy-300 hover:text-navy-700"
              )}
            >
              {EMPLOYMENT_TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="rounded-xl2 border border-dashed border-line bg-mist px-6 py-16 text-center text-sm text-muted">
          該当する求人はありません。
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
          {/* 求人カードの見出しは h3。その前に h2 が無いページで
              見出しレベルが飛ばないよう、読み上げ専用の見出しを置く。 */}
          <h2 className="sr-only">求人一覧</h2>
          {filtered.map((job) => (
            <JobCardItem key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}
